<template>

	<div class="list-container">
		<div v-for="(item, index) in currItems" :key="item.uniqueId ? item.uniqueId : index" class="list-item" :class="{highZ: item.highZ}" :style="itemBackground(item)">
			<slot :name="item.customSlot" :item="item" :index="index">
				<div class="list-item-header" :class="{clickable: !!item.chevronItem}" @click="clickOnRow(item)">
					<p class="list-item-title" :class="{grey: !!item.leftGreyText}">{{ item.title }}</p>
					<AppSwitch class="right-item" v-if="item.switch" :disabled="!!item.disabled" :checked="!!item.active" @switched="(newVal)=>itemSwitched(item, newVal)" :ref="item.ref ? item.ref : 'switch'" />
					<AppSelect class="right-item" v-else-if="item.selectValues" :values="item.selectValues" :selected-index="item.selectedIndex" @selected="item.callbackSelect" />
					<AppNumberInput class="right-item" v-else-if="item.numberInput" :symbol="item.symbol ? item.symbol : null" :right-side-symbol="!!item.rightSymbol" :value="item.value" :min="(item.min || item.min === 0) ? item.min : null" :max="item.max ? item.max : null" :double-decimal="item.doubleDecimal ? item.doubleDecimal : false" @changed-value="item.callbackNumber" />
					<AppTextInput class="right-item text-wrapper" v-else-if="item.textInput" :textarea="!!item.isTextArea" :placeholder="item.placeholder || item.value" :value="item.value" :type="item.inputType ? item.inputType : 'text'" @changed-text="item.callbackText" />
					<AppButton class="right-item" v-else-if="item.buttonTitle" small outline :text="item.buttonTitle" :disabled="!!item.buttonDisabled" :loading="!!item.buttonLoading" @click="item.callbackButton" />
					<AppColorPicker class="right-item" v-else-if="item.colorPicker" :value="item.color" @changed-color="item.callbackColor" />
					<AppChevronIcon class="right-item chevron-icon" v-else-if="item.chevronItem" :direction="item.active ? 'up' : 'down'" />
					<AppDropzone class="right-item" v-else-if="item.imageSlot" :max-bytes="item.maxBytes" :max-width="item.maxWidth ? item.maxWidth : 1024" :max-height="item.maxHeight ? item.maxHeight : 1024" :files="item.image ? [item.image] : []" @removed-file="item.callbackRemove" @upload-complete="item.callbackImage" />
					<div class="right-item text-value" :class="{primary: !!item.rightPrimaryText}" v-else-if="item.rightText">{{ item.rightText }}</div>
				</div>
			</slot>
			<slot :name="item.headerSlot" :item="item" :index="index">
				<div v-if="item.explain" class="extra-text">
					<p>{{item.explain}}</p>
				</div>
			</slot>
			<AppDropdown v-if="item.dropdownSlot" :collapsed="!item.active">
				<slot :name="item.dropdownSlot" :item="item" :index="index" />
			</AppDropdown>
		</div>
	</div>

</template>

<script>
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppColorPicker from "@/shared/components/AppColorPicker.vue";
import AppDropzone from "@/shared/components/AppDropzone.vue";

export default {
	name: "AppList",
	components: {AppDropzone, AppColorPicker, AppChevronIcon, AppTextInput, AppNumberInput, AppSwitch, AppDropdown, AppSelect, AppButton},
	props: {
		items: {type: Array, required: true, validator(value) {
			if(value && value.length) {
				return value.length > 0 && value.every(item => {
					return item.hasOwnProperty('title');
				});
			} else {
				return true;
			}
		}},
		backgroundColor: {type: String, default: 'transparent'},
	},
	data: function () {
		return {
			currItems: this.items,
		};
	},
	methods: {
		itemBackground(item) {
			return {
				'--list-item-background-color': item.backgroundColor ? item.backgroundColor : 'transparent'
			}
		},
		itemSwitched(item, newVal) {
			item.active = newVal;
			if(item.callback) {
				item.callback(newVal);
			}
		},
		clickOnRow(item) {
			if(item.chevronItem) {
				this.itemSwitched(item, !item.active);
			}
		}
	},
	watch: {
		items(newItems) {
			this.currItems = newItems;
		}
	}
}
</script>

<style lang="scss" scoped>

.list-container {
	width: 100%;

	.list-item {
		@include row;
		@include justifyFlex(start);
		@include alignFlex(center);
		width: 100%;
		padding: 8px 12px;
		position: relative;
		min-height: 51px;
		background-color: var(--list-item-background-color);

		&.highZ {
			z-index: 100000;
		}

		.list-item-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			flex-wrap: wrap;
			width: 100%;
			gap: 8px;

			&.clickable {
				cursor: pointer;
			}

			.right-item {
				margin-left: auto;
			}

			.select-wrapper {
				max-width: 190px;
			}

			.text-wrapper {
				width: 240px;
			}

			@media (max-width: 1250px) {
				.text-wrapper {
					width: 190px;
				}
			}
		}

		&:before, &:after {
			@include pseudo;
			top: calc(100% - 1px);
			left: 0;
			height: 1px;
			width: 100%;
			background-color: var(--color-border-alpha);
		}

		&:before {
			top: 0;
		}

		&:not(:nth-of-type(1)):before {
			display: none;
		}

		&:first-of-type:before,
		&:last-of-type:after {
			display: none;
		}

		.list-item-title {
			@include wordWrap;
			font-size: 16px;

			&.grey {
				font-size: 14px;
				color: #9b9b9b;
			}
		}

		.text-value {
			font-size: 16px;
			text-align: right;

			&.primary {
				color: $primary;
			}
		}

		@media (max-width: 768px) {
			padding: 8px;
		}
		@media (max-width: 560px) {
			.list-item-title {
				font-size: 14px;
			}
		}

		.extra-text {
			width: 100%;
			margin-top: 6px;

			p {
				font-size: 14px;
				line-height: 16px;
				color: #9B9B9B;
			}

			@media(max-width: 400px) {
				p {
					font-size: 12px;
				}
			}

		}

		.chevron-icon {
			margin: 12px 0;
		}

	}
}

</style>